// Demo Mode route policy. Patient pages and the NPI-keyed provider network
// page carry a real identifier in their path, which the address bar, history
// and link-hover UI show outside the DOM mask. While Demo Mode protects, those
// routes carry a stand-in token instead of the identifier: the URL reads
// /patients/demo~<fake id>, where the fake is the same format-preserving fake
// the page shows for that identifier. The route's props function turns the
// token back into the real identifier, so fetching and business logic never
// see the token. Entity-id routes such as /caremanagement/patients/:id use an
// internal surrogate key, not a patient identifier, and are untouched.
//
// A route is identifying when it RESOLVES to a record with one of these
// params, so the policy follows the router's own matching (case-insensitive
// paths, optional trailing slash, percent-encoding) instead of a separate
// pattern that could disagree with it. main.js binds the app router; the
// pattern fallback exists only for contexts without one.
//
// Token rules:
//   - The marker prefix `demo~` can never start a real identifier (no MBI,
//     NPI or patient identifier contains "~"), and any param starting with it
//     is treated as a token, never as a real value.
//   - token -> real lives in this tab's memory and in localStorage (one key
//     per token), so a new tab or a reload opened on a token URL while Demo
//     Mode protects can resolve it. A resolved entry is only accepted if the
//     token's fake part is the fake of that real value.
//   - An unknown token (stale link, Demo Mode exited, another browser) goes to
//     a neutral "link expired" page. It is NEVER read as a real identifier:
//     its fake part may be another real patient's identifier.
//   - The token table is cleared when the last protecting tab exits, when a
//     presenter starts a fresh session with no other tab protecting, and at a
//     boot that is not protected (no protecting tab exists anywhere).
//
// Routes opt in with `meta: { demoTokens: true }`, a name, and
// `props: demoRouteProps`. An identifying route without the opt-in still goes
// to the neutral DemoModeUnavailable page while protected.

import { isDemoMode, spoofText } from '@/utils/demoSpoof';

export const DEMO_UNAVAILABLE_ROUTE_NAME = 'DemoModeUnavailable';
export const DEMO_LINK_EXPIRED_ROUTE_NAME = 'DemoModeLinkExpired';
export const DEMO_TOKEN_PREFIX = 'demo~';
export const DEMO_TOKEN_STORAGE_PREFIX = 'demoModeRouteToken:';

const IDENTIFYING_PARAMS = ['patientIdentifier', 'npi'];

// The identifying path segment is captured so a token there is recognized.
const FALLBACK_PATHS = [
  /^\/+patients\/+([^/?#]+)/i,
  /^\/+external\/+patient\/+([^/?#]+)/i,
  /^\/+provider\/+network\/+([^/?#]+)/i,
];

let boundRouter = null;

export function bindDemoRouter(router) {
  boundRouter = router || null;
}

// --- Tokens ---

// Who may use tokens: registered by the controller as
// demoModeAllowedForCurrentUser -- true (a superuser is signed in), false (a
// signed-in user who is not one) or null (nobody known yet). A token resolves
// only for true. While the user is unknown a token route is let through to
// the auth guard and decided after it (demoRouteRecheck); a user who is not a
// superuser never resolves one, so a demo~ URL is "link expired" for them and
// nothing derived from a token reaches a page, a request or a saved path.
let tokenAccess = () => true;

export function setRouteTokenAccess(fn) {
  tokenAccess = typeof fn === 'function' ? fn : () => true;
}

function tokensResolve() {
  return tokenAccess() === true;
}

const tokenToReal = new Map(); // `${param}:${token}` -> real
const realToToken = new Map(); // `${param}:${real}` -> token

export function isRouteToken(value) {
  return typeof value === 'string' && value.toLowerCase().startsWith(DEMO_TOKEN_PREFIX);
}

function fakeFor(real) {
  return spoofText(String(real), 'identifier') || 'id';
}

// FNV-1a, two seeds, as base36: a short tag that is a pure function of the
// real value.
function hashTag(value, length) {
  let out = '';
  for (let seed = 0x811c9dc5, round = 0; out.length < length; round += 1, seed ^= 0x9e3779b9) {
    let h = seed >>> 0;
    const text = `${round}|${value}`;
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    out += h.toString(36).padStart(7, '0');
  }
  return out.slice(0, length);
}

// The tokens a real value may have, shortest first: its on-screen fake plus a
// tag derived from the real value. Deterministic, so a token always names the
// same record, in any session or tab, whatever order tokens were made in; two
// records that share a fake differ in the tag. The longer forms exist only for
// a tag collision.
function tokenCandidates(param, real) {
  const value = String(real);
  const fake = fakeFor(value);
  return [4, 8, 12].map((n) => `${DEMO_TOKEN_PREFIX}${fake}.${hashTag(`${param}|${value}`, n)}`);
}

// The token a real value gets when nothing collides (tests and fixtures).
export function routeTokenName(param, real) {
  return tokenCandidates(param, real)[0];
}

function storageKey(param, token) {
  return `${DEMO_TOKEN_STORAGE_PREFIX}${param}:${token}`;
}

function readStored(param, token) {
  try {
    return localStorage.getItem(storageKey(param, token));
  } catch {
    return null;
  }
}

function writeStored(param, token, real) {
  try {
    localStorage.setItem(storageKey(param, token), real);
    return localStorage.getItem(storageKey(param, token)) === real;
  } catch {
    // Without storage the token still works in this tab; other tabs and a
    // reload show "link expired" rather than anything identifying.
    return true;
  }
}

function remember(param, token, real) {
  tokenToReal.set(`${param}:${token}`, real);
  realToToken.set(`${param}:${real}`, token);
}

// The stand-in for a real identifier, created on first use (never for a user
// known not to be a superuser).
export function tokenFor(param, real) {
  if (tokenAccess() === false) return null;
  const value = String(real);
  const known = realToToken.get(`${param}:${value}`);
  if (known) return known;
  for (const token of tokenCandidates(param, value)) {
    const owner = tokenToReal.get(`${param}:${token}`) ?? readStored(param, token);
    if (owner === value) {
      remember(param, token, value);
      return token;
    }
    if (owner != null) continue;
    if (!writeStored(param, token, value)) continue;
    remember(param, token, value);
    return token;
  }
  return null;
}

// The real identifier behind a token, or null: only for a signed-in
// superuser, and only when the token is exactly one that real value would
// get. Never derives anything from the token itself.
export function resolveRouteToken(param, token) {
  if (!isRouteToken(token) || !tokensResolve()) return null;
  const key = `${param}:${token}`;
  let real = tokenToReal.get(key);
  if (real == null) {
    real = readStored(param, token);
    if (real == null) return null;
    if (!tokenCandidates(param, real).includes(token)) return null;
    remember(param, token, real);
  }
  return real;
}

export function clearRouteTokens() {
  tokenToReal.clear();
  realToToken.clear();
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(DEMO_TOKEN_STORAGE_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    /* nothing stored */
  }
}

// --- Route classification ---

function hasParam(value) {
  if (Array.isArray(value)) return value.some((v) => v != null && v !== '');
  return value != null && value !== '';
}

function identifyingParamsOf(route) {
  const params = route?.params;
  if (!params) return [];
  return IDENTIFYING_PARAMS.filter((name) => hasParam(params[name]));
}

function isTokenValue(value) {
  return Array.isArray(value) ? value.every((v) => !hasParam(v) || isRouteToken(v)) : isRouteToken(value);
}

// True when the route shows a real identifier (a token does not count).
export function isIdentifyingRoute(route) {
  return identifyingParamsOf(route).some((name) => !isTokenValue(route.params[name]));
}

function supportsTokens(route) {
  return Boolean(route?.meta?.demoTokens && route.name);
}

function locationWith(route, params) {
  return { name: route.name, params: { ...route.params, ...params }, query: route.query, hash: route.hash };
}

// The same route with every real identifying param replaced by its token, or
// null when the route cannot carry tokens.
function tokenizedLocation(route) {
  if (!supportsTokens(route)) return null;
  const params = {};
  for (const name of identifyingParamsOf(route)) {
    const value = route.params[name];
    if (Array.isArray(value)) return null;
    if (isRouteToken(value)) continue;
    const token = tokenFor(name, value);
    if (!token) return null;
    params[name] = token;
  }
  return locationWith(route, params);
}

// The same route with every token resolved, or null when a token is unknown.
function realLocation(route) {
  const params = {};
  for (const name of identifyingParamsOf(route)) {
    const value = route.params[name];
    if (!isRouteToken(value)) continue;
    const real = resolveRouteToken(name, value);
    if (real == null) return null;
    params[name] = real;
  }
  return locationWith(route, params);
}

function hasToken(route) {
  return identifyingParamsOf(route).some((name) => isRouteToken(route.params[name]));
}

function safeDecode(path) {
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

export function isIdentifyingPath(path) {
  if (!path) return false;
  if (boundRouter) {
    try {
      return isIdentifyingRoute(boundRouter.resolve(String(path)));
    } catch {
      /* fall through to the pattern check */
    }
  }
  const bare = safeDecode(String(path).split(/[?#]/)[0]);
  return FALLBACK_PATHS.some((re) => {
    const m = bare.match(re);
    return Boolean(m) && !isRouteToken(m[1]);
  });
}

function sameOriginUrl(href) {
  let url;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }
  return url.origin === window.location.origin ? url : null;
}

export function isIdentifyingHref(href) {
  if (!href) return false;
  const url = sameOriginUrl(href);
  return url ? isIdentifyingPath(url.pathname + url.search) : false;
}

// demoSpoof's link rewriter: a tokenized href for an identifying link, false
// for one that cannot be tokenized (its href is removed), null to leave it.
export function tokenizeHref(href) {
  if (!href) return null;
  const url = sameOriginUrl(href);
  if (!url) return null;
  const path = url.pathname + url.search + url.hash;
  if (!boundRouter) return isIdentifyingPath(path) ? false : null;
  let route;
  try {
    route = boundRouter.resolve(path);
  } catch {
    return isIdentifyingPath(path) ? false : null;
  }
  if (!isIdentifyingRoute(route)) return null;
  const location = tokenizedLocation(route);
  if (!location) return false;
  try {
    return boundRouter.resolve(location).href;
  } catch {
    return false;
  }
}

// Props for a token-capable route: the route's params with tokens resolved to
// the real identifier. An unresolvable token is dropped rather than passed
// on (the guard has already sent such a navigation to "link expired").
export function demoRouteProps(route) {
  const params = { ...route.params };
  for (const name of IDENTIFYING_PARAMS) {
    if (!isRouteToken(params[name])) continue;
    const real = resolveRouteToken(name, params[name]);
    if (real == null) delete params[name];
    else params[name] = real;
  }
  return params;
}

export const demoUnavailableRoute = {
  path: '/demo-unavailable',
  name: DEMO_UNAVAILABLE_ROUTE_NAME,
  component: () => import('@/components/DemoMode/DemoModeUnavailable.vue'),
  meta: { requiresAuth: true, layout: 'default' },
  // Only meaningful while Demo Mode is on. Reached with it off (a typed URL,
  // or last_visited_route restored after exiting on this page), go home.
  beforeEnter: () => (isDemoMode() ? true : { path: '/', replace: true }),
};

export const demoLinkExpiredRoute = {
  path: '/demo-link-expired',
  name: DEMO_LINK_EXPIRED_ROUTE_NAME,
  component: () => import('@/components/DemoMode/DemoModeUnavailable.vue'),
  props: { reason: 'expired' },
  meta: { requiresAuth: true, layout: 'default' },
  // Shown when a demo~ link could not be resolved. Reached any other way with
  // Demo Mode off (a typed URL, a saved last_visited_route), go home.
  beforeEnter: (to) => (isDemoMode() || isTokenRoute(to.redirectedFrom) ? true : { path: '/', replace: true }),
};

// True when the browser's address bar already shows `to`: a back/forward
// (popstate) or initial navigation, as opposed to a push or replace that has
// not changed the URL yet.
function addressBarShows(to) {
  if (!boundRouter || typeof window === 'undefined') return false;
  try {
    const here = boundRouter.resolve(window.location.pathname + window.location.search + window.location.hash);
    return (
      here.name === to.name &&
      IDENTIFYING_PARAMS.every((name) => String(here.params[name] ?? '') === String(to.params[name] ?? ''))
    );
  } catch {
    return false;
  }
}

// Router beforeEach hook: returns a redirect, or null to continue. `to` is
// already resolved by the router.
//   protected, real identifier, token-capable route -> the tokenized route.
//     A push stays a push (back returns to the list page). When the address
//     bar already holds the real path (back/forward, initial load) the token
//     REPLACES that entry, so history keeps the token and never the real
//     identifier, and forward history survives.
//   protected, real identifier, other route         -> DemoModeUnavailable
//   any mode, unknown token                         -> link expired
//   ordinary mode, known token                      -> the real route (replace)
export function demoRouteRedirect(to) {
  if (!identifyingParamsOf(to).length) return null;
  if (hasToken(to)) {
    // Before anyone is signed in the auth guard has not run yet: decide in
    // demoRouteRecheck, after it.
    if (tokenAccess() == null) return null;
    return tokenRouteRedirect(to);
  }
  if (!isDemoMode() || !isIdentifyingRoute(to)) return null;
  const tokenized = tokenizedLocation(to);
  if (!tokenized) return { name: DEMO_UNAVAILABLE_ROUTE_NAME, replace: true };
  return addressBarShows(to) ? { ...tokenized, replace: true } : tokenized;
}

// A token route, once the user is known: a superuser stays on it while
// protected and goes to the real route otherwise. For anyone else the one
// token this tab minted at boot goes back to the URL the tab loaded (see the
// boot record below); every other token, or one the table no longer has, is
// "link expired".
function tokenRouteRedirect(to) {
  const real = realLocation(to);
  if (!real) return bootRecordRedirect(to) ?? { name: DEMO_LINK_EXPIRED_ROUTE_NAME, replace: true };
  if (!isDemoMode()) return { ...real, replace: true };
  return null;
}

// Router beforeResolve hook: runs after the auth guard has resolved the
// signed-in user. A token route that can no longer be resolved (the user
// turned out not to be a superuser, or the table was cleared meanwhile) goes
// to "link expired" before anything mounts or fetches.
export function demoRouteRecheck(to) {
  if (!hasToken(to)) return null;
  return tokenRouteRedirect(to);
}

// Boot record: which real URL this tab swapped for a stand-in at boot.
//
// While nobody is identified yet, secureInitialUrl shows the token in the
// address bar (a presenter's real identifier must never be on a shared
// screen). If the tab is reloaded in that window, the reload has only the
// token, and a customer -- who can never resolve tokens -- would get "link
// expired" for their own link. So the tab keeps one record in sessionStorage
// (per tab; never shared): { token, real }. When the user turns out not to be a
// superuser, exactly that token goes back to exactly that real URL, on the
// same page load or after a reload in this tab. No other token resolves, a
// token copied to another tab or browser is still "link expired", and a
// superuser never uses the record (their token resolves from the table).
//
// The record is read into memory at boot, before sign-in can revoke Demo
// Mode, and that copy lives only until a navigation settles with the user
// known (or it is used once). The stored copy is removed then, and on
// revoke, logout and Demo Mode exit. The key sits outside the demoMode*
// namespace on purpose: it is not Demo Mode state, and must stay readable
// when Demo Mode's own keys are not.
export const DEMO_BOOT_RECORD_KEY = 'demoBootRecord';
let bootRecord = null;

function readBootRecord() {
  try {
    const raw = sessionStorage.getItem(DEMO_BOOT_RECORD_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    return record && typeof record.token === 'string' && typeof record.real === 'string' ? record : null;
  } catch {
    return null;
  }
}

function writeBootRecord(record) {
  try {
    sessionStorage.setItem(DEMO_BOOT_RECORD_KEY, JSON.stringify(record));
  } catch {
    /* without it a reload in the sign-in window shows "link expired" */
  }
}

// Removes the stored record (revoke, logout, exit). The in-memory copy for
// this page load's first navigation is left alone: revoke runs inside the
// auth guard, before the token route is decided.
export function clearBootRecord() {
  try {
    sessionStorage.removeItem(DEMO_BOOT_RECORD_KEY);
  } catch {
    /* nothing stored */
  }
}

function forgetBootRecord() {
  bootRecord = null;
  clearBootRecord();
}

function bootRecordRedirect(to) {
  if (!bootRecord || tokenAccess() !== false || to.fullPath !== bootRecord.token) return null;
  const real = bootRecord.real;
  forgetBootRecord();
  return { path: real, replace: true };
}

// Boot step (main.js, right after initDemoMode): when Demo Mode is on at boot
// and the page was opened on a real identifying URL, rewrite the address bar
// to the tokenized URL synchronously, before the app renders anything, and
// point the router's own history state (if a previous visit left one) at the
// token too.
//
// The router itself does not navigate to the token: it read the initial
// location when it was created, before this runs, so the first navigation
// targets the real path and is decided after the auth guard. A customer on a
// browser with leftover Demo Mode state therefore lands on their own page (the
// address bar shows the stand-in only until that navigation commits), and a
// superuser is redirected onto the stand-in before the page renders. A reload
// in that window boots on the token; the boot record covers it.
export function secureInitialUrl(router) {
  bootRecord = null;
  if (typeof window === 'undefined') return;
  try {
    const here = window.location.pathname + window.location.search + window.location.hash;
    const saved = readBootRecord();
    if (saved && router.resolve(here).fullPath === saved.token) bootRecord = saved;
    else if (saved) clearBootRecord();
    const settle = router.afterEach(() => {
      if (tokenAccess() == null) return;
      forgetBootRecord();
      settle();
    });
    if (!isDemoMode()) return;
    const redirect = demoRouteRedirect(router.resolve(here));
    if (!redirect) return;
    const target = router.resolve(redirect);
    const state = window.history.state && typeof window.history.state === 'object'
      ? { ...window.history.state, current: target.fullPath }
      : window.history.state;
    window.history.replaceState(state, '', target.href);
    if (hasToken(target)) {
      bootRecord = { token: target.fullPath, real: router.resolve(here).fullPath };
      writeBootRecord(bootRecord);
    }
  } catch {
    /* the router guard still redirects before anything renders */
  }
}

// Demo Mode activation step: make the current route safe before the
// controller reports ready (entering Demo Mode on an already-open patient
// deep link, or booting on one).
export async function secureCurrentRoute(router) {
  await router.isReady();
  // The first navigation waits for the signed-in user, and a user who is not
  // a superuser turns Demo Mode off meanwhile: their page stays where it is.
  if (!isDemoMode()) return;
  const redirect = demoRouteRedirect(router.currentRoute.value);
  if (!redirect) return;
  await router.replace({ ...redirect, replace: true });
  if (isIdentifyingRoute(router.currentRoute.value)) {
    throw new Error('Demo Mode could not leave the patient page');
  }
}

// True when the route carries a stand-in token.
export function isTokenRoute(route) {
  return Boolean(route) && hasToken(route);
}

// Demo Mode exit step: a tokenized current route goes back to its real path.
// The location is computed synchronously, before the token table can be
// cleared; the navigation itself completes afterwards.
export function restoreCurrentRoute(router) {
  const route = router?.currentRoute?.value;
  if (!route || !hasToken(route)) return null;
  const real = realLocation(route);
  // No real path to go to: re-run the guards on this token route, which send
  // it to "link expired" with the token as the reason.
  return router.replace(real ?? { path: route.fullPath, force: true }).catch(() => {});
}

// The path to persist for a route (post-login restore): the real path, never
// a token that may have expired by the time it is used.
//
// A token is turned back into the real path only for a signed-in superuser.
// For anyone else (nobody signed in yet, or a customer) the token path itself
// is kept, so after sign-in demoRouteRecheck decides: the real page for a
// superuser, "link expired" for anyone else.
export function persistablePath(route) {
  if (!route || !hasToken(route) || !boundRouter) return route?.fullPath;
  if (!tokensResolve()) return route.fullPath;
  const real = realLocation(route);
  if (!real) return '/';
  try {
    return boundRouter.resolve(real).fullPath;
  } catch {
    return '/';
  }
}
